import { useTranslation } from 'next-i18next/pages';
import { getExposePath, type SearchListing } from '@pkgs/api';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useSimilarListings } from '@/hooks/queries/useSimilarListings';
import { useListing } from '@/hooks/queries/useListing';
import { PropertyPreview } from '@/components/property-expose/PropertyPreview';

type IntroProps = React.ComponentProps<typeof PropertyPreview>['intro'];

export const SimilarProperties = ({ similarListings }: { similarListings: SearchListing[] }) => {
  const { t: tPrivateOffice } = useTranslation([TRANSLATION_NAMESPACE.private_office]);
  const { t: tPropertyExpose } = useTranslation([TRANSLATION_NAMESPACE.property_expose]);
  const { countryCode, language } = useSettings();
  const { data: similarProperties } = useSimilarListings();
  const {
    data: { genericListing },
  } = useListing();
  const { trackExposeNavigationEvent } = useExposeTracking();
  const { pageVariant } = useExposePageProps();

  if (similarListings.length === 0) return null;

  const intro: IntroProps = {
    headline: pageVariant.includes('private-office')
      ? tPrivateOffice('private_office:search.expose.similarProjects.privateOffice.label.headline', {
          agentName: genericListing.agent?.name,
        })
      : tPropertyExpose('search.expose.similarObjects.label.headline'),
    overline: pageVariant.includes('private-office') ? undefined : tPropertyExpose('search.expose.similarObjects.label.overline'),
    verticalLineTop: true,
  };
  if (!similarProperties) return null;

  return (
    <PropertyPreview
      properties={similarListings}
      intro={intro}
      isSlider={true}
      sliderControls={{ next: 'next', previous: 'previous' }}
      data-testid="expose-similar-properties"
      // unfortunately these props aren't optional in the spearhead component
      button={{
        label: '',
        href: '',
      }}
      maxNumberProperties={12}
      onCardClick={(index) => {
        const property = similarListings[index];
        if (!property) return undefined;

        const exposePath = getExposePath(property, language, countryCode);
        trackExposeNavigationEvent({
          targetUrl: exposePath,
          elementId: `similar-exposes_preview-${index + 1}_link`,
        });
      }}
    />
  );
};
